<template>
  <div v-if="hasAdminAccess">
    <div class="usage-metrics">
        <TabView>
        <TabPanel header="Metrics">
          <div class="timeframe-selector">
            <label for="timeframe">Time Period:</label>
            <select id="timeframe" v-model="selectedTimeframe" @change="loadMetrics">
              <option v-for="timeframe in timeframes" :key="timeframe.value" :value="timeframe.value">
                {{ timeframe.label }}
              </option>
            </select>
            
            <div v-if="selectedTimeframe === 'custom'" class="custom-date-range">
              <div class="date-input">
                <label for="start-date">Start Date:</label>
                <input 
                  type="date" 
                  id="start-date" 
                  v-model="customStartDate"
                  :max="customEndDate || new Date().toISOString().split('T')[0]"
                >
              </div>
              <div class="date-input">
                <label for="end-date">End Date:</label>
                <input 
                  type="date" 
                  id="end-date" 
                  v-model="customEndDate"
                  :min="customStartDate"
                  :max="new Date().toISOString().split('T')[0]"
                >
              </div>
              <Button
                @click="loadMetrics"
                :disabled="!customStartDate || !customEndDate || customStartDate > customEndDate"
                style="align-self: flex-end; height: 2.5rem; margin-left: 1rem;"
              >
                Apply
              </Button>
            </div>
          </div>

          <h2>Active Users</h2>
          <div class="charts-container">
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.dau.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('dau')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.dau" />
              <Chart v-else type="line" :data="dauChartData" :options="chartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.wau.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('wau')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.wau" />
              <Chart v-else type="line" :data="wauChartData" :options="chartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.mau.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('mau')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.mau" />
              <Chart v-else type="line" :data="mauChartData" :options="chartOptions" />
            </div>
          </div>

          <h2 class="mt-4">Token Usage - Text</h2>
          <div class="charts-container">
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.totalTextTokens.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('totalTextTokens')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.totalTokens" />
              <Chart v-else type="line" :data="totalTextTokensChartData" :options="chartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.userTextTokens.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('userTextTokens')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.userTokens" />
              <Chart v-else type="line" :data="userTextTokensChartData" :options="userTokensChartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.userGroupTextTokens.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('userGroupTextTokens')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.userGroupTokens" />
              <Chart v-else type="line" :data="userGroupTextTokensChartData" :options="userGroupTokensChartOptions" />
            </div>
          </div>

          <h2 class="mt-4">Token Usage - Vector</h2>
          <div class="charts-container">
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.totalVectorTokens.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('totalVectorTokens')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.totalTokens" />
              <Chart v-else type="line" :data="totalVectorTokensChartData" :options="chartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.userVectorTokens.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('userVectorTokens')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.userTokens" />
              <Chart v-else type="line" :data="userVectorTokensChartData" :options="userTokensChartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.userGroupVectorTokens.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('userGroupVectorTokens')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.userGroupTokens" />
              <Chart v-else type="line" :data="userGroupVectorTokensChartData" :options="userGroupTokensChartOptions" />
            </div>
          </div>

          <h2 class="mt-4">Average Token Usage</h2>
          <div class="charts-container">
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.userMessageTokens.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('userMessageTokens')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.messageTokens" />
              <Chart v-else type="line" :data="userMessageTokensChartData" :options="chartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.botMessageTokens.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('botMessageTokens')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.messageTokens" />
              <Chart v-else type="line" :data="botMessageTokensChartData" :options="chartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.conversationTokens.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('conversationTokens')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.conversationTokens" />
              <Chart v-else type="line" :data="conversationTokensChartData" :options="chartOptions" />
            </div>
          </div>

          <h2 class="mt-4">Conversation Metrics</h2>
          <div class="charts-container">
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.conversationResponses.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('conversationResponses')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.conversationResponses" />
              <Chart v-else type="line" :data="conversationResponsesChartData" :options="chartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.conversationCount.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('conversationCount')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.conversationCount" />
              <Chart v-else type="line" :data="conversationCountChartData" :options="chartOptions" />
            </div>
          </div>

          <h2 class="mt-4">Message Metrics</h2>
          <div class="charts-container">
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.totalMessages.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('totalMessages')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.totalMessages" />
              <Chart v-else type="line" :data="totalMessagesChartData" :options="chartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.userMessages.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('userMessages')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.userMessages" />
              <Chart v-else type="line" :data="userMessagesChartData" :options="userTokensChartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.userGroupMessages.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('userGroupMessages')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.userGroupMessages" />
              <Chart v-else type="line" :data="userGroupMessagesChartData" :options="userGroupTokensChartOptions" />
            </div>
          </div>

          <h2 class="mt-4">Top Agents</h2>
          <div class="charts-container">
            <div class="chart-card" style="max-width: 50%;">
              <div class="chart-header">
                <h3>{{ chartConfigs.topSkills.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('topSkills')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.topSkills" />
              <Chart v-else type="pie" :data="topSkillsChartData" :options="topSkillsChartOptions" />
            </div>
            </div>

          <h2 class="mt-4">Files</h2>
          <div class="charts-container">
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.fileSizeByExtension.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('fileSizeByExtension')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.fileSizeByExtension" />
              <Chart v-else type="bar" :data="fileSizeByExtensionChartData" :options="fileSizeByExtensionChartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.fileCountByExtension.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('fileCountByExtension')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.fileCountByExtension" />
              <Chart v-else type="bar" :data="fileCountByExtensionChartData" :options="fileCountByExtensionChartOptions" />
            </div>
          </div>

          <h2 class="mt-4">SharePoint</h2>
          <div class="charts-container">
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.sharepointSites.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('sharepointSites')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.sharepointSites" />
              <Chart v-else type="bar" :data="sharepointSitesChartData" :options="sharepointSitesChartOptions" />
            </div>
            <div class="chart-card">
              <div class="chart-header">
                <h3>{{ chartConfigs.sharepointSync.title }}</h3>
                <button class="maximize-btn" @click="maximizeChart('sharepointSync')" title="Maximize">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                </button>
              </div>
              <ProgressSpinnerContainer v-if="loading.sharepointDiscoveredFiles || loading.sharepointSyncedFiles || loading.sharepointProcessableFiles || loading.sharepointProcessedFiles" />
              <Chart v-else type="bar" :data="sharepointSyncChartData" :options="sharepointSyncChartOptions" />
            </div>
          </div>
        </TabPanel>
        <TabPanel header="Events">
          <div>
            <div class="events-filters">
              <label for="log-type-filter">Filter by Event Type:</label>
              <select 
                id="log-type-filter" 
                v-model="selectedLogType"
                :disabled="loading.logTypes"
              >
                <option :value="null">All Event Types</option>
                <option v-for="logType in logTypes" :key="logType" :value="logType">
                  {{ logType }}
                </option>
              </select>
              
              <label for="description-filter">Filter by Description:</label>
              <input 
                type="text" 
                id="description-filter" 
                v-model="descriptionFilter"
                placeholder="Enter description text..."
                :disabled="loading.logTypes"
              >

            </div>
            <div class="events-filters">
              
              <label for="event-start-date">Start Date/Time:</label>
              <input 
                type="datetime-local" 
                id="event-start-date" 
                v-model="eventStartDate"
                :max="eventEndDate || new Date().toISOString().slice(0, 16)"
                :disabled="loading.logTypes"
              >
              
              <label for="event-end-date">End Date/Time:</label>
              <input 
                type="datetime-local" 
                id="event-end-date" 
                v-model="eventEndDate"
                :min="eventStartDate"
                :max="new Date().toISOString().slice(0, 16)"
                :disabled="loading.logTypes"
              >
              
              <Button
                label="Clear Filters"
                @click="clearEventFilters"
                class="p-button-secondary"
                :disabled="loading.logTypes"
              />
              
              <Button
                label="Apply Filters"
                @click="applyEventFilters"
                class="p-button-primary"
                :disabled="loading.logTypes"
              />
            </div>
            <DataTable
              :value="events"
              lazy paginator
              :loading="loading.events"
              :first="eventsFirst" :rows="25"
              :totalRecords="eventsTotalCount"
              @page="eventsOnPage($event)"
            >
              <Column field="log_type" header="Event Type"></Column>
              <Column field="created_on" header="Date">
                <template #body="slotProps">
                  {{ formatEventDate(slotProps.data.created_on) }}
                </template>
              </Column>
              <Column field="description" header="Description"></Column>
              <Column header="Actions">
                <template #body="slotProps">
                  <Button
                    label="Details"
                    @click="openEventDetails(slotProps.data)"
                    class="p-button-sm p-button-outlined"
                    size="small"
                  />
                </template>
              </Column>
            </DataTable>
          </div>
        </TabPanel>
        <TabPanel header="Credit Usage">
          <div>
            <div class="timeframe-selector">
              <label for="credits-timeframe">Time Period:</label>
              <select id="credits-timeframe" v-model="creditsTimeframe" @change="onCreditsTimeframeChange">
                <option v-for="tf in timeframes" :key="tf.value" :value="tf.value">
                  {{ tf.label }}
                </option>
              </select>

              <div v-if="creditsTimeframe === 'custom'" class="custom-date-range">
                <div class="date-input">
                  <label for="credits-start-date">Start Date:</label>
                  <input
                    type="date"
                    id="credits-start-date"
                    v-model="creditsStartDate"
                    :max="creditsEndDate || new Date().toISOString().split('T')[0]"
                  >
                </div>
                <div class="date-input">
                  <label for="credits-end-date">End Date:</label>
                  <input
                    type="date"
                    id="credits-end-date"
                    v-model="creditsEndDate"
                    :min="creditsStartDate"
                    :max="new Date().toISOString().split('T')[0]"
                  >
                </div>
                <Button
                  @click="loadCredits"
                  :disabled="!creditsStartDate || !creditsEndDate || creditsStartDate > creditsEndDate"
                  style="align-self: flex-end; height: 2.5rem; margin-left: 1rem;"
                >
                  Apply
                </Button>
              </div>
            </div>

            <ProgressSpinnerContainer v-if="loading.credits" />
            <div v-else class="credits-dashboard">
              <div
                v-for="item in creditsDashboard"
                :key="item.category"
                class="credits-dashboard-card"
                @click="openCreditsByCategory(item)"
              >
                <div class="credits-dashboard-card-title">{{ item.categoryDisplay }}</div>
                <div class="credits-dashboard-card-value">{{ formatCredits(item.total_credits_spent) }} Credits</div>
                <div class="credits-dashboard-card-hint">Click to view usage details</div>
              </div>
            </div>
          </div>
        </TabPanel>
        <TabPanel header="Files">
          <div>
            <div class="files-section">
              <div class="file-directories-container">
                <FileTreeSelector 
                  :showFileDetails="true"
                  @node-click="onNodeDoubleClick"
                />
              </div>
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>

    <!-- Maximized Chart Modal -->
    <Dialog :visible="!!maximizedChart" @update:visible="closeMaximizedChart">
      <template #header>
        <h2>{{ maximizedChartTitle || 'Chart' }}</h2>
      </template>
      <Chart
        :type="maximizedChartType"
        :data="maximizedChartData"
        :options="maximizedChartOptions"
      />
    </Dialog>

    <!-- Event Details Modal -->
    <Dialog :visible="showEventDetailsModal" @update:visible="closeEventDetails">
      <template #header>
        <h2>Event Details</h2>
      </template>
      <div v-if="selectedEvent" class="event-details">
        <div class="event-detail-row">
          <strong>Event Type: </strong>
          <span>{{ selectedEvent.log_type }}</span>
        </div>
        <div class="event-detail-row">
          <strong>Date: </strong>
          <span>{{ formatEventDate(selectedEvent.created_on) }}</span>
        </div>
        <div class="event-detail-row">
          <strong>Description: </strong>
          <span>{{ selectedEvent.description }}</span>
        </div>
        <div v-if="selectedEvent.metadata" class="event-detail-row">
          <strong>Metadata: </strong>
          <div class="metadata-content">
            <pre>{{ JSON.stringify(selectedEvent.metadata, null, 2) }}</pre>
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Credits Detail Modal -->
    <Dialog :visible="showCreditsDetailModal" @update:visible="closeCreditsDetail">
      <template #header>
        <h2>
          {{ creditsModalView === 'users' ? 'Credit Usage by User' : (selectedCreditsCategory?.category === 'ACTION' ? 'Credit Usage by Type' : 'Credit Usage by Model') }}
          {{ selectedCreditsCategory ? ` - ${selectedCreditsCategory.categoryDisplay}` : '' }}
          {{ selectedCreditsModel ? ` / ${selectedCreditsModel.model_name}` : '' }}
          {{ getCreditsDateRangeDisplay() ? ` (${getCreditsDateRangeDisplay()})` : '' }}
        </h2>
      </template>
      <template #footer v-if="creditsModalView === 'models'">
        <Button label="Back" icon="pi pi-arrow-left" @click="closeCreditsDetail" class="p-button-secondary p-button-sm" />
      </template>
      <template #footer v-else-if="creditsModalView === 'users'">
        <Button label="Back to Models" icon="pi pi-arrow-left" @click="creditsModalBackToModels" class="p-button-secondary p-button-sm" />
      </template>
          <ScrollPanel v-if="creditsModalView === 'models'" class="scroll-panel" style="max-height: 60vh">
            <DataTable :value="creditsByModel">
              <Column v-if="selectedCreditsCategory?.category !== 'ACTION'" field="model_name" header="Model" />
              <Column field="total_usage_amount" :header="selectedCreditsCategory?.category === 'ACTION' ? 'Total Actions' : 'Usage Amount'">
                <template #body="slotProps">
                  {{ formatCredits(slotProps.data.total_usage_amount) }}
                </template>
              </Column>
              <Column field="total_credits_spent" header="Credits Used">
                <template #body="slotProps">
                  {{ formatCredits(slotProps.data.total_credits_spent) }}
                </template>
              </Column>
              <Column header="Actions">
                <template #body="slotProps">
                  <Button
                    label="View by User"
                    @click="openCreditsByModel(slotProps.data)"
                    class="p-button-sm p-button-outlined"
                    size="small"
                  />
                </template>
              </Column>
            </DataTable>
          </ScrollPanel>
      <ScrollPanel v-else-if="creditsModalView === 'users' && selectedCreditsModel?.users" class="scroll-panel" style="max-height: 60vh">
        <DataTable :value="creditsUsersSorted">
          <Column field="email" header="User">
            <template #body="slotProps">
              {{ slotProps.data.email || 'Data Processing' }}
            </template>
          </Column>
          <Column v-if="selectedCreditsCategory?.category !== 'ACTION'" field="usage_type" header="Usage Type">
            <template #body="slotProps">
              {{ formatUsageType(slotProps.data.usage_type || 'Other') }}
            </template>
          </Column>
          <Column field="total_usage_amount" :header="selectedCreditsCategory?.category === 'ACTION' ? 'Total Actions' : 'Usage Amount'">
            <template #body="slotProps">
              {{ formatCredits(slotProps.data.total_usage_amount) }}
            </template>
          </Column>
          <Column field="total_credits_spent" header="Credits Used">
            <template #body="slotProps">
              {{ formatCredits(slotProps.data.total_credits_spent) }}
            </template>
          </Column>
        </DataTable>
      </ScrollPanel>
    </Dialog>

    <!-- File Details Modal -->
    <Dialog :visible="showFileDetailsModal" @update:visible="closeFileDetails"
            header="File Details">
      <ScrollPanel class="scroll-panel">
        <div v-if="selectedFile" class="file-details">
          <div class="mb">
          <Button
              label="Download File" 
              :disabled="downloadingFile"
              @click="downloadFile(selectedFile.id, selectedFile.filename)"
              severity="info"
            />
          <Button
              class="ml"
              v-if="selectedFile.metadata?.webUrl"
              label="Open in SharePoint" 
              @click="openExternal(selectedFile.metadata.webUrl)"
              severity="info"
            />  
          <Button
              class="ml"
              v-if="selectedFile.metadata?.webViewLink"
              label="Open in Google Drive" 
              @click="openExternal(selectedFile.metadata.webViewLink)"
              severity="info"
            />  
          </div>
          <div class="file-detail-row">
            <strong>Filename: </strong>
            <span>{{ selectedFile.filename }}</span>
          </div>
          <div class="file-detail-row">
            <strong>File Type: </strong>
            <span>{{ getFileType(selectedFile) }}</span>
          </div>
          <div class="file-detail-row">
            <strong>File Size: </strong>
            <span>{{ formatFileSize(selectedFile.metadata?.size) }}</span>
          </div>
          <div class="file-detail-row">
            <strong>Sync Status: </strong>
            <span>{{ getSyncStatus(selectedFile.metadata) }}</span>
          </div>
          <div class="file-detail-row">
            <strong>Last Synchronized: </strong>
            <span>{{ formatEventDate(selectedFile.updated_on ? selectedFile.updated_on : selectedFile.created_on) }}</span>
          </div>
          <div v-if="selectedFile.file_path" class="file-detail-row">
            <strong>Path: </strong>
            <span>{{ selectedFile.file_path }}</span>
          </div>
          <div class="file-detail-row">
            <strong>Access Grants: </strong>
            <div class="access-grants-content">
              <ProgressSpinnerContainer v-if="loadingAccessGrants" />
              <DataTable v-else :value="fileAccessGrants" class="access-grants-table">
                <Column field="metadata.email" header="User Email">
                  <template #body="slotProps">
                    {{ slotProps.data.metadata?.email || 'N/A' }}
                  </template>
                </Column>
                <Column field="access" header="Access Level">
                  <template #body="slotProps">
                    {{ getAccessLabel(slotProps.data) }}
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
          <div v-if="selectedFile.metadata" class="file-detail-row">
            <strong>Metadata: </strong>
            <div class="metadata-content">
              <pre class="metadata-pre">{{ JSON.stringify(selectedFile.metadata, null, 2) }}</pre>
            </div>
          </div>
        </div>
      </ScrollPanel>
    </Dialog>
  </div>
  <div v-else>
    You do not have permission to access this page.
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { get, apiGetAll } from '../util/api.js';
import { selectedAccount } from '../store/state.js';
import ProgressSpinnerContainer from '../components/ProgressSpinnerContainer.vue';
import FileTreeSelector from '../components/FileTreeSelector.vue';
import { useUsers, useModelDeployments, useSkills, useKnowledgeSources } from '../composables/entities.js';
import Chart from 'primevue/chart';
import ScrollPanel from 'primevue/scrollpanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

const { hasAdminAccess, users, userGroups } = useUsers();
const { skills } = useSkills();
const { knowledgeSources } = useKnowledgeSources();
const { modelDeployments } = useModelDeployments();

const loading = ref({
  dau: true,
  wau: true,
  mau: true,
  totalTokens: true,
  userTokens: true,
  userGroupTokens: true,
  messageTokens: true,
  conversationTokens: true,
  conversationResponses: true,
  conversationCount: true,
  totalMessages: true,
  userMessages: true,
  userGroupMessages: true,
  topSkills: true,
  fileSizeByExtension: true,
  fileCountByExtension: true,
  sharepointSites: true,
  sharepointDiscoveredFiles: true,
  sharepointSyncedFiles: true,
  sharepointProcessableFiles: true,
  sharepointProcessedFiles: true,

  events: true,
  logTypes: true,

  credits: true
});

const events = ref([]);
const eventsFirst = ref(0);
const eventsTotalCount = ref(0);
const logTypes = ref([]);
const selectedLogType = ref(null);
const descriptionFilter = ref('');
const eventStartDate = ref(null);
const eventEndDate = ref(null);
const showEventDetailsModal = ref(false);
const selectedEvent = ref(null);
const showFileDetailsModal = ref(false);
const selectedFile = ref(null);

const creditsRaw = ref([]);
const creditsTimeframe = ref('7d');
const creditsStartDate = ref(null);
const creditsEndDate = ref(null);
const showCreditsDetailModal = ref(false);

function formatEventDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString();
}

function eventsOnPage($event) {
  eventsFirst.value = $event.first;
  loadEvents();
}

const dailyActiveUsers = ref([]);
const weeklyActiveUsers = ref([]);
const monthlyActiveUsers = ref([]);
const totalTokens = ref([]);
const userTokens = ref([]);
const userGroupTokens = ref([]);
const messageTokens = ref([]);
const conversationTokens = ref([]);
const conversationResponses = ref([]);
const conversationCount = ref([]);
const totalMessages = ref([]);
const userMessages = ref([]);
const userGroupMessages = ref([]);
const topSkills = ref([]);
const fileSizeByExtension = ref([]);
const fileCountByExtension = ref([]);
const sharepointSites = ref([]);
const sharepointDiscoveredFiles = ref([]);
const sharepointSyncedFiles = ref([]);
const sharepointProcessableFiles = ref([]);
const sharepointProcessedFiles = ref([]);

const selectedTimeframe = ref('7d'); // Default to 7 days
const customStartDate = ref(null);
const customEndDate = ref(null);
const timeframes = [
  { label: 'Last 24 Hours', value: '24h' },
  { label: 'Last 7 Days', value: '7d' },
  { label: 'Last 30 Days', value: '30d' },
  { label: 'Custom', value: 'custom' }
];

const OUTPUT_CATEGORY_DISPLAY = {
  TEXT: 'LLM Tokens',
  SUMMARIZATION: 'Summarization',
  DOCUMENT_INTELLIGENCE: 'Document Intelligence',
  VECTOR: 'Embeddings',
  ACTION: 'Agentic Actions',
  API_CALL: 'API Calls'
};

const OUTPUT_CATEGORY_ORDER = ['TEXT', 'VECTOR', 'DOCUMENT_INTELLIGENCE', 'SUMMARIZATION', 'ACTION', 'API_CALL'];

const creditsDashboard = computed(() => {
  const raw = creditsRaw.value;
  const byCategory = new Map();

  // Initialize all categories with zero values
  OUTPUT_CATEGORY_ORDER.forEach(cat => {
    byCategory.set(cat, {
      category: cat,
      categoryDisplay: OUTPUT_CATEGORY_DISPLAY[cat] || cat,
      total_usage_amount: 0,
      total_credits_spent: 0,
      dollars: 0,
      rows: []
    });
  });

  for (const row of raw) {
    const usageType = (row.usage_type || row.USAGE_TYPE || '').toUpperCase();

    // Special handling for ACTION category - filter by USAGE_TYPE
    if (usageType === 'ACTION') {
      const g = byCategory.get('ACTION');
      g.total_usage_amount += Number(row.total_usage_amount) || 0;
      g.total_credits_spent += Number(row.total_credits_spent) || 0;
      g.dollars += Number(row.dollars) || 0;
      g.rows.push(row);
    } else {
      // Regular categories - use OUTPUT_CATEGORY but exclude ACTION usage types
      const cat = (row.output_category || row.OUTPUT_CATEGORY || 'OTHER').toUpperCase();
      if (byCategory.has(cat)) {
        const g = byCategory.get(cat);
        g.total_usage_amount += Number(row.total_usage_amount) || 0;
        g.total_credits_spent += Number(row.total_credits_spent) || 0;
        g.dollars += Number(row.dollars) || 0;
        g.rows.push(row);
      }
    }
  }

  return OUTPUT_CATEGORY_ORDER.map(cat => byCategory.get(cat));
});

const creditsByModel = computed(() => {
  const category = selectedCreditsCategory.value;
  if (!category?.rows) return [];

  // Special handling for ACTION category - single aggregated row for entire timeframe
  if (category.category === 'ACTION') {
    const aggregated = {
      model_name: 'Agentic Actions',
      total_usage_amount: 0,
      total_credits_spent: 0,
      users: []
    };
    for (const row of category.rows) {
      aggregated.total_usage_amount += Number(row.total_usage_amount) || 0;
      aggregated.total_credits_spent += Number(row.total_credits_spent) || 0;
      aggregated.users.push(row);
    }
    return [aggregated];
  }

  // Regular categories - group by model and deployment (entire timeframe, no per-day)
  const byModel = new Map();
  for (const row of category.rows) {
    const key = `${row.model_name || ''}|${row.deployment_id || ''}`;
    if (!byModel.has(key)) {
      byModel.set(key, {
        model_name: row.model_name || 'Unknown',
        deployment_id: row.deployment_id,
        total_usage_amount: 0,
        total_credits_spent: 0,
        users: []
      });
    }
    const g = byModel.get(key);
    g.total_usage_amount += Number(row.total_usage_amount) || 0;
    g.total_credits_spent += Number(row.total_credits_spent) || 0;
    g.users.push(row);
  }
  const result = Array.from(byModel.values());
  result.sort((a, b) => (b.total_credits_spent || 0) - (a.total_credits_spent || 0));
  return result;
});

const creditsUsersSorted = computed(() => {
  const users = selectedCreditsModel.value?.users;
  const category = selectedCreditsCategory.value;
  if (!users?.length) return [];

  // Group by user (and usage type for non-ACTION) - aggregate entire timeframe into one row
  const byKey = new Map();
  for (const row of users) {
    const email = row.email || row.cephisso_user_id || 'Data Processing';
    const usageType = (row.usage_type || row.USAGE_TYPE || '').toUpperCase();
    const key = category?.category === 'ACTION' ? email : `${email}|${usageType || 'OTHER'}`;
    if (!byKey.has(key)) {
      byKey.set(key, {
        email,
        usage_type: usageType || null,
        total_usage_amount: 0,
        total_credits_spent: 0
      });
    }
    const g = byKey.get(key);
    g.total_usage_amount += Number(row.total_usage_amount) || 0;
    g.total_credits_spent += Number(row.total_credits_spent) || 0;
  }
  const result = Array.from(byKey.values());
  result.sort((a, b) => (b.total_credits_spent || 0) - (a.total_credits_spent || 0));
  return result;
});

const creditsModalView = ref(null);
const selectedCreditsCategory = ref(null);
const selectedCreditsModel = ref(null);

// Maximize chart functionality
const maximizedChart = ref(null);
const maximizedChartTitle = ref('');
const maximizedChartType = ref('line');
const maximizedChartData = ref({});
const maximizedChartOptions = ref({});

// Chart configuration for maximize functionality
const chartConfigs = {
  dau: { title: 'Daily Active Users', type: 'line', data: 'dauChartData', options: 'chartOptions' },
  wau: { title: 'Weekly Active Users', type: 'line', data: 'wauChartData', options: 'chartOptions' },
  mau: { title: 'Monthly Active Users', type: 'line', data: 'mauChartData', options: 'chartOptions' },
  totalTextTokens: { title: 'Total Text Tokens', type: 'line', data: 'totalTextTokensChartData', options: 'chartOptions' },
  userTextTokens: { title: 'Text Tokens by User', type: 'line', data: 'userTextTokensChartData', options: 'userTokensChartOptions' },
  userGroupTextTokens: { title: 'Text Tokens by User Group', type: 'line', data: 'userGroupTextTokensChartData', options: 'userGroupTokensChartOptions' },
  totalVectorTokens: { title: 'Total Vector Tokens', type: 'line', data: 'totalVectorTokensChartData', options: 'chartOptions' },
  userVectorTokens: { title: 'Vector Tokens by User', type: 'line', data: 'userVectorTokensChartData', options: 'userTokensChartOptions' },
  userGroupVectorTokens: { title: 'Vector Tokens by User Group', type: 'line', data: 'userGroupVectorTokensChartData', options: 'userGroupTokensChartOptions' },
  userMessageTokens: { title: 'Average User Message Tokens', type: 'line', data: 'userMessageTokensChartData', options: 'chartOptions' },
  botMessageTokens: { title: 'Average Bot Response Tokens', type: 'line', data: 'botMessageTokensChartData', options: 'chartOptions' },
  conversationTokens: { title: 'Average Tokens per Conversation', type: 'line', data: 'conversationTokensChartData', options: 'chartOptions' },
  conversationResponses: { title: 'Average Responses per Conversation', type: 'line', data: 'conversationResponsesChartData', options: 'chartOptions' },
  conversationCount: { title: 'Number of Conversations', type: 'line', data: 'conversationCountChartData', options: 'chartOptions' },
  totalMessages: { title: 'Total Messages per Day', type: 'line', data: 'totalMessagesChartData', options: 'chartOptions' },
  userMessages: { title: 'Messages per User', type: 'line', data: 'userMessagesChartData', options: 'userTokensChartOptions' },
  userGroupMessages: { title: 'Messages per User Group', type: 'line', data: 'userGroupMessagesChartData', options: 'userGroupTokensChartOptions' },
  topSkills: { title: 'Most Used Skills', type: 'pie', data: 'topSkillsChartData', options: 'topSkillsChartOptions' },
  fileSizeByExtension: { title: 'Average File Size by Extension', type: 'bar', data: 'fileSizeByExtensionChartData', options: 'fileSizeByExtensionChartOptions' },
  fileCountByExtension: { title: 'File Count by Extension', type: 'bar', data: 'fileCountByExtensionChartData', options: 'fileCountByExtensionChartOptions' },
  sharepointSites: { title: 'SharePoint Sites by Knowledge Source', type: 'bar', data: 'sharepointSitesChartData', options: 'sharepointSitesChartOptions' },
  sharepointSync: { title: 'File Sync Progress by Knowledge Source', type: 'bar', data: 'sharepointSyncChartData', options: 'sharepointSyncChartOptions' }
};

const chartOptions = {
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0
      }
    }
  }
};

const userTokensChartOptions = {
  plugins: {
    legend: {
      display: true,
      position: 'right'
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0
      }
    }
  }
};

const userGroupTokensChartOptions = {
  plugins: {
    legend: {
      display: true,
      position: 'right'
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0
      }
    }
  }
};

const topSkillsChartOptions = {
  plugins: {
    legend: {
      display: true,
      position: 'right',
      labels: {
        usePointStyle: true,
        pointStyle: 'rect'
      }
    }
  }
};

const topSkillsBarChartOptions = {
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    x: {
      grid: {
        display: false
      }
    },
    y: {
      beginAtZero: true,
      grid: {
        display: false
      },
      ticks: {
        precision: 0
      }
    }
  }
};

const dauChartData = computed(() => {
  const filledData = fillMissingDates(dailyActiveUsers.value, 'date', 'users', currentDateRange.value);
  return {
    labels: filledData.map(item => formatDate(item.date)),
    datasets: [{
      data: filledData.map(item => item.users),
      borderColor: '#60A5F9',
      tension: 0.4,
      fill: false
    }]
  };
});

const wauChartData = computed(() => {
  const filledData = fillMissingDates(weeklyActiveUsers.value, 'date', 'users', currentDateRange.value);
  return {
    labels: filledData.map(item => formatDate(item.date)),
    datasets: [{
      data: filledData.map(item => item.users),
      borderColor: '#60A5F9',
      tension: 0.4,
      fill: false
    }]
  };
});

const mauChartData = computed(() => {
  const filledData = fillMissingDates(monthlyActiveUsers.value, 'date', 'users', currentDateRange.value);
  return {
    labels: filledData.map(item => formatDate(item.date)),
    datasets: [{
      data: filledData.map(item => item.users),
      borderColor: '#60A5F9',
      tension: 0.4,
      fill: false
    }]
  };
});

const totalTextTokensChartData = computed(() => {
  const textTokens = totalTokens.value.filter(item => item.output_category === 'TEXT');
  const filledData = fillMissingDates(textTokens, 'date', 'tokens_used', currentDateRange.value);
  
  return {
    labels: filledData.map(item => formatDate(item.date)),
    datasets: [{
      label: 'Text Tokens',
      data: filledData.map(item => item.tokens_used),
      borderColor: '#60A5F9',
      tension: 0.4,
      fill: false
    }]
  };
});

const totalVectorTokensChartData = computed(() => {
  const vectorTokens = totalTokens.value.filter(item => item.output_category === 'VECTOR');
  const filledData = fillMissingDates(vectorTokens, 'date', 'tokens_used', currentDateRange.value);
  
  return {
    labels: filledData.map(item => formatDate(item.date)),
    datasets: [{
      label: 'Vector Tokens',
      data: filledData.map(item => item.tokens_used),
      borderColor: '#34D399',
      tension: 0.4,
      fill: false
    }]
  };
});

const userTextTokensChartData = computed(() => {
  const textTokens = userTokens.value.filter(item => item.output_category === 'TEXT');
  const userIds = [...new Set(textTokens.map(item => item.user_id))].filter(id => id !== null);
  
  const createUserDataset = (userId) => {
    const user = userId ? users.value.find(u => String(u.id) === String(userId)) : null;
    const label = userId ? (user ? user.name : `User ${userId}`) : 'System User';
    
    const userData = textTokens.filter(item => String(item.user_id) === String(userId));
    const filledData = fillMissingDates(userData, 'date', 'tokens_used', currentDateRange.value);
    
    return {
      label,
      data: filledData.map(item => item.tokens_used),
      tension: 0.4,
      fill: false,
      borderColor: userId ? undefined : '#60A5F9'
    };
  };

  return {
    labels: currentDateRange.value.map(date => formatDate(date)),
    datasets: [
      createUserDataset(null),
      ...userIds.map(userId => createUserDataset(userId))
    ]
  };
});

const userVectorTokensChartData = computed(() => {
  const vectorTokens = userTokens.value.filter(item => item.output_category === 'VECTOR');
  const userIds = [...new Set(vectorTokens.map(item => item.user_id))].filter(id => id !== null);
  
  const createUserDataset = (userId) => {
    const user = userId ? users.value.find(u => String(u.id) === String(userId)) : null;
    const label = userId ? (user ? user.name : `User ${userId}`) : 'System User';
    
    const userData = vectorTokens.filter(item => String(item.user_id) === String(userId));
    const filledData = fillMissingDates(userData, 'date', 'tokens_used', currentDateRange.value);
    
    return {
      label,
      data: filledData.map(item => item.tokens_used),
      tension: 0.4,
      fill: false,
      borderColor: userId ? undefined : '#34D399'
    };
  };

  return {
    labels: currentDateRange.value.map(date => formatDate(date)),
    datasets: [
      createUserDataset(null),
      ...userIds.map(userId => createUserDataset(userId))
    ]
  };
});

const userGroupTextTokensChartData = computed(() => {
  const textTokens = userGroupTokens.value.filter(item => item.output_category === 'TEXT');
  const groupIds = [...new Set(textTokens.map(item => item.cephisso_platform_user_groups_id))]
    .filter(id => id !== null);
  
  const createGroupDataset = (groupId) => {
    const group = userGroups.value.find(g => String(g.id) === String(groupId));
    
    const groupData = textTokens.filter(item => String(item.cephisso_platform_user_groups_id) === String(groupId));
    const filledData = fillMissingDates(groupData, 'date', 'tokens_used', currentDateRange.value);
    
    return {
      label: group ? group.name : `Group ${groupId}`,
      data: filledData.map(item => item.tokens_used),
      tension: 0.4,
      fill: false
    };
  };

  return {
    labels: currentDateRange.value.map(date => formatDate(date)),
    datasets: groupIds.map(groupId => createGroupDataset(groupId))
  };
});

const userGroupVectorTokensChartData = computed(() => {
  const vectorTokens = userGroupTokens.value.filter(item => item.output_category === 'VECTOR');
  const groupIds = [...new Set(vectorTokens.map(item => item.cephisso_platform_user_groups_id))]
    .filter(id => id !== null);
  
  const createGroupDataset = (groupId) => {
    const group = userGroups.value.find(g => String(g.id) === String(groupId));
    
    const groupData = vectorTokens.filter(item => String(item.cephisso_platform_user_groups_id) === String(groupId));
    const filledData = fillMissingDates(groupData, 'date', 'tokens_used', currentDateRange.value);
    
    return {
      label: group ? group.name : `Group ${groupId}`,
      data: filledData.map(item => item.tokens_used),
      tension: 0.4,
      fill: false
    };
  };

  return {
    labels: currentDateRange.value.map(date => formatDate(date)),
    datasets: groupIds.map(groupId => createGroupDataset(groupId))
  };
});

const userMessageTokensChartData = computed(() => {
  const userTokens = messageTokens.value.filter(item => item.message_type === 'user');
  const filledData = fillMissingDates(userTokens, 'date', 'tokens_used', currentDateRange.value);
  
  return {
    labels: filledData.map(item => formatDate(item.date)),
    datasets: [{
      label: 'Average User Message Tokens',
      data: filledData.map(item => item.tokens_used),
      borderColor: '#60A5F9',
      tension: 0.4,
      fill: false
    }]
  };
});

const botMessageTokensChartData = computed(() => {
  const botTokens = messageTokens.value.filter(item => item.message_type === 'bot');
  const filledData = fillMissingDates(botTokens, 'date', 'tokens_used', currentDateRange.value);
  
  return {
    labels: filledData.map(item => formatDate(item.date)),
    datasets: [{
      label: 'Average Bot Response Tokens',
      data: filledData.map(item => item.tokens_used),
      borderColor: '#34D399',
      tension: 0.4,
      fill: false
    }]
  };
});

const conversationTokensChartData = computed(() => {
  const filledData = fillMissingDates(conversationTokens.value, 'date', 'tokens_used', currentDateRange.value);
  
  return {
    labels: filledData.map(item => formatDate(item.date)),
    datasets: [{
      label: 'Average Tokens per Conversation',
      data: filledData.map(item => item.tokens_used),
      borderColor: '#F59E0B',
      tension: 0.4,
      fill: false
    }]
  };
});

const conversationResponsesChartData = computed(() => {
  const filledData = fillMissingDates(conversationResponses.value, 'date', 'responses', currentDateRange.value);
  
  return {
    labels: filledData.map(item => formatDate(item.date)),
    datasets: [{
      label: 'Average Responses per Conversation',
      data: filledData.map(item => item.responses),
      borderColor: '#8B5CF6',
      tension: 0.4,
      fill: false
    }]
  };
});

const conversationCountChartData = computed(() => {
  const filledData = fillMissingDates(conversationCount.value, 'date', 'conversations', currentDateRange.value);
  
  return {
    labels: filledData.map(item => formatDate(item.date)),
    datasets: [{
      label: 'Number of Conversations',
      data: filledData.map(item => item.conversations),
      borderColor: '#8B5CF6',
      tension: 0.4,
      fill: false
    }]
  };
});

const totalMessagesChartData = computed(() => {
  const filledData = fillMissingDates(totalMessages.value, 'date', 'messages', currentDateRange.value);
  
  return {
    labels: filledData.map(item => formatDate(item.date)),
    datasets: [{
      label: 'Total Messages',
      data: filledData.map(item => item.messages),
      borderColor: '#EC4899',
      tension: 0.4,
      fill: false
    }]
  };
});

const userMessagesChartData = computed(() => {
  const userIds = [...new Set(userMessages.value.map(item => item.user_id))].filter(id => id !== null);
  
  const createUserDataset = (userId) => {
    const user = userId ? users.value.find(u => String(u.id) === String(userId)) : null;
    const label = userId ? (user ? user.name : `User ${userId}`) : 'System User';
    
    const userData = userMessages.value.filter(item => String(item.user_id) === String(userId));
    const filledData = fillMissingDates(userData, 'date', 'messages', currentDateRange.value);
    
    return {
      label,
      data: filledData.map(item => item.messages),
      tension: 0.4,
      fill: false,
      borderColor: userId ? undefined : '#EC4899'
    };
  };

  return {
    labels: currentDateRange.value.map(date => formatDate(date)),
    datasets: [
      createUserDataset(null),
      ...userIds.map(userId => createUserDataset(userId))
    ]
  };
});

const userGroupMessagesChartData = computed(() => {
  const groupIds = [...new Set(userGroupMessages.value.map(item => item.cephisso_platform_user_groups_id))]
    .filter(id => id !== null);
  
  const createGroupDataset = (groupId) => {
    const group = userGroups.value.find(g => String(g.id) === String(groupId));
    
    const groupData = userGroupMessages.value.filter(item => String(item.cephisso_platform_user_groups_id) === String(groupId));
    const filledData = fillMissingDates(groupData, 'date', 'messages', currentDateRange.value);
    
    return {
      label: group ? group.name : `Group ${groupId}`,
      data: filledData.map(item => item.messages),
      tension: 0.4,
      fill: false
    };
  };

  return {
    labels: currentDateRange.value.map(date => formatDate(date)),
    datasets: groupIds.map(groupId => createGroupDataset(groupId))
  };
});

const topSkillsChartData = computed(() => {
  const topFiveSkills = (topSkills.value.slice(0, 5) ?? []).reverse();
  
  // If no data, show a placeholder
  if (topFiveSkills.length === 0) {
    return {
      labels: ['No Data'],
      datasets: [{
        label: 'Usage Count',
        data: [0],
        backgroundColor: ['#E5E7EB'], // Light gray
        borderWidth: 0
      }]
    };
  }
  
  return {
    labels: topFiveSkills.map(item => {
      const skill = skills.value.find(s => String(s.id) === String(item.skill_id));
      return skill ? skill.name : `Skill ${item.skill_id}`;
    }),
    datasets: [{
      label: 'Usage Count',
      data: topFiveSkills.map(item => item.count),
      backgroundColor: [
        '#60A5F9',  // blue
        '#34D399',  // green
        '#F59E0B',  // orange
        '#EC4899',  // pink
        '#8B5CF6'   // purple
      ],
      borderWidth: 0
    }]
  };
});

const fileSizeByExtensionChartData = computed(() => ({
  labels: fileSizeByExtension.value.map(item => item.extension),
  datasets: [{
    label: 'Average File Size (bytes)',
    data: fileSizeByExtension.value.map(item => item.avg_size),
    backgroundColor: [
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40'
    ]
  }]
}))

const fileSizeByExtensionChartOptions = {
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          const value = context.raw
          const sizeInMB = (value / (1024 * 1024)).toFixed(2)
          return `Average Size: ${sizeInMB} MB`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: function(value) {
          const sizeInMB = (value / (1024 * 1024)).toFixed(2)
          return `${sizeInMB} MB`
        }
      }
    }
  }
}

const fileCountByExtensionChartData = computed(() => ({
  labels: fileCountByExtension.value.map(item => item.extension),
  datasets: [{
    label: 'Total File Count',
    data: fileCountByExtension.value.map(item => item.count),
    backgroundColor: [
      '#FF6384',
      '#36A2EB',
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40'
    ]
  }]
}));

const fileCountByExtensionChartOptions = {
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          const value = context.raw;
          return `${context.chart.data.labels[context.dataIndex]}: ${value.toLocaleString()} files`;
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: function(value) {
          return value.toLocaleString();
        }
      }
    }
  }
};

const sharepointSitesChartData = computed(() => {
  const knowledgeSourceMap = new Map(knowledgeSources.value.map(ks => [ks.id, ks.name]))
  
  return {
    labels: sharepointSites.value.map(item => knowledgeSourceMap.get(item.id) || `Knowledge Source ${item.id}`),
    datasets: [
      {
        label: 'Enabled Sites',
        data: sharepointSites.value.map(item => item.enabled_sites_count),
        backgroundColor: '#4BC0C0'
      },
      {
        label: 'Partially Enabled Sites',
        data: sharepointSites.value.map(item => item.partial_site_count),
        backgroundColor: '#FFCE56'
      }
    ]
  }
})

const sharepointSitesChartOptions = {
  plugins: {
    legend: {
      display: true,
      position: 'right'
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          const value = context.raw
          return `${context.dataset.label}: ${value.toLocaleString()} sites`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: function(value) {
          return value.toLocaleString()
        }
      }
    }
  }
}

const sharepointSyncChartData = computed(() => {
  const knowledgeSourceMap = new Map(knowledgeSources.value.map(ks => [ks.id, ks.name]))
  
  // Get unique knowledge source IDs from all metrics, excluding null/undefined IDs
  const knowledgeSourceIds = new Set([
    ...sharepointDiscoveredFiles.value.filter(item => item.knowledge_source_id).map(item => item.knowledge_source_id),
    ...sharepointSyncedFiles.value.filter(item => item.knowledge_source_id).map(item => item.knowledge_source_id),
    ...sharepointProcessableFiles.value.filter(item => item.knowledge_source_id).map(item => item.knowledge_source_id),
    ...sharepointProcessedFiles.value.filter(item => item.knowledge_source_id).map(item => item.knowledge_source_id)
  ])

  // Create a map for each metric type, excluding null/undefined IDs
  const discoveredMap = new Map(
    sharepointDiscoveredFiles.value
      .filter(item => item.knowledge_source_id)
      .map(item => [item.knowledge_source_id, item.count])
  )
  const syncedMap = new Map(
    sharepointSyncedFiles.value
      .filter(item => item.knowledge_source_id)
      .map(item => [item.knowledge_source_id, item.count])
  )
  const processableMap = new Map(
    sharepointProcessableFiles.value
      .filter(item => item.knowledge_source_id)
      .map(item => [item.knowledge_source_id, item.count])
  )
  const processedMap = new Map(
    sharepointProcessedFiles.value
      .filter(item => item.knowledge_source_id)
      .map(item => [item.knowledge_source_id, item.count])
  )

  // Convert to arrays for the chart, using friendly names
  const sortedIds = Array.from(knowledgeSourceIds)
    .sort((a, b) => (knowledgeSourceMap.get(+a) || '').localeCompare(knowledgeSourceMap.get(+b) || ''))
  
  const labels = sortedIds.map(id => knowledgeSourceMap.get(+id) || `Knowledge Source ${id}`)
  
  return {
    labels,
    datasets: [
      {
        label: 'Discovered',
        data: sortedIds.map(id => discoveredMap.get(id) || 0),
        backgroundColor: '#FF6384'
      },
      {
        label: 'Synced',
        data: sortedIds.map(id => syncedMap.get(id) || 0),
        backgroundColor: '#36A2EB'
      },
      {
        label: 'Processable',
        data: sortedIds.map(id => processableMap.get(id) || 0),
        backgroundColor: '#FFCE56'
      },
      {
        label: 'Processed',
        data: sortedIds.map(id => processedMap.get(id) || 0),
        backgroundColor: '#4BC0C0'
      }
    ]
  }
})

const sharepointSyncChartOptions = {
  plugins: {
    legend: {
      display: true,
      position: 'right'
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          const value = context.raw
          return `${context.dataset.label}: ${value.toLocaleString()} files`
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: function(value) {
          return value.toLocaleString()
        }
      }
    }
  }
}

function formatDate(dateString) {
  if (dateString) {
    return dateString.substring(0, dateString.length - 10);
  }
  return '';
}

function generateDateRange(startDate, endDate) {
  const dates = [];
  const current = new Date(startDate);
  const end = new Date(endDate);
  
  while (current <= end) {
    // Format as YYYY-MM-DDTHH:mm:ssZ to match backend format
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}T00:00:00Z`);
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
}

function fillMissingDates(data, dateField, valueField, dateRange) {
  const dateMap = new Map();
  
  // Create a map of existing data
  data.forEach(item => {
    const date = item[dateField];
    if (date) {
      dateMap.set(date, item[valueField] || 0);
    }
  });
  
  // Fill in missing dates with zero values
  return dateRange.map(date => ({
    [dateField]: date,
    [valueField]: dateMap.get(date) || 0
  }));
}

function getDateRange() {
  let end = new Date();
  let start = new Date();

  if (selectedTimeframe.value === 'custom') {
    if (!customStartDate.value || !customEndDate.value) {
      return null;
    }
    start = new Date(customStartDate.value);
    end = new Date(customEndDate.value);
  } else {
    const value = parseInt(selectedTimeframe.value);
    if (selectedTimeframe.value.endsWith('h')) {
      start.setHours(start.getHours() - value);
    } else if (selectedTimeframe.value.endsWith('d')) {
      start.setDate(start.getDate() - value);
    }
  }

  return {
    start: start.toISOString(),
    end: end.toISOString()
  };
}

function getCreditsDateRange() {
  let end = new Date();
  let start = new Date();

  if (creditsTimeframe.value === 'custom') {
    if (!creditsStartDate.value || !creditsEndDate.value) {
      return null;
    }
    start = new Date(creditsStartDate.value);
    end = new Date(creditsEndDate.value);
  } else {
    const value = parseInt(creditsTimeframe.value);
    if (creditsTimeframe.value.endsWith('h')) {
      start.setHours(start.getHours() - value);
    } else if (creditsTimeframe.value.endsWith('d')) {
      start.setDate(start.getDate() - value);
    }
  }

  return {
    start: start.toISOString(),
    end: end.toISOString()
  };
}

function onCreditsTimeframeChange() {
  if (creditsTimeframe.value !== 'custom') {
    loadCredits();
  }
}

async function loadCredits() {
  const dateRange = getCreditsDateRange();
  if (!dateRange) return;

  loading.value.credits = true;
  try {
    const query = `q={"$and":[{"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}}},{"day":{"$gte":{"$date":"${dateRange.start}"},"$lte":{"$date":"${dateRange.end}"}}}]}`;
    const response = await apiGetAll('/crbn-tech/usage/credits?limit=5000&' + query);
    creditsRaw.value = response || [];
  } catch (error) {
    console.error('Error loading credits:', error);
  } finally {
    loading.value.credits = false;
  }
}

function openCreditsByCategory(category) {
  selectedCreditsCategory.value = category;
  selectedCreditsModel.value = null;
  creditsModalView.value = 'models';
  showCreditsDetailModal.value = true;
}

function openCreditsByModel(model) {
  selectedCreditsModel.value = model;
  creditsModalView.value = 'users';
}

function creditsModalBackToModels() {
  selectedCreditsModel.value = null;
  creditsModalView.value = 'models';
}

function closeCreditsDetail() {
  showCreditsDetailModal.value = false;
  creditsModalView.value = null;
  selectedCreditsCategory.value = null;
  selectedCreditsModel.value = null;
}

function formatCredits(value) {
  if (value == null || value === '' || isNaN(value)) return '0';
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function formatUsageType(value) {
  if (!value) return 'Other';
  return String(value)
    .split(/[\s_]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function getCreditsDateRangeDisplay() {
  const range = getCreditsDateRange();
  if (!range) return '';
  const start = new Date(range.start).toLocaleDateString();
  const end = new Date(range.end).toLocaleDateString();
  return `${start} - ${end}`;
}

const currentDateRange = computed(() => {
  const range = getDateRange();
  if (!range) return [];
  return generateDateRange(range.start, range.end);
});


async function loadMetrics() {
  const dateRange = getDateRange();
  if (!dateRange) return;

  const query = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}}}&start_date=${dateRange.start}&end_date=${dateRange.end}`;
  
  // Load all metrics independently
  loadDAU(query);
  loadWAU(query);
  loadMAU(query);
  loadTotalTokens(query);
  loadUserTokens(query);
  loadUserGroupTokens(query);
  loadMessageTokens(query);
  loadConversationTokens(query);
  loadConversationResponses(query);
  loadConversationCount(query);
  loadTotalMessages(query);
  loadUserMessages(query);
  loadUserGroupMessages(query);
  loadTopSkills(query);
  loadFileSizeByExtension(query);
  loadFileCountByExtension(query);
  loadSharePointSites(query);
  loadSharePointFiles(query);
}

async function loadEvents() {
  loading.value.events = true;
  try {
    // Convert datetime-local values (user's local time) to UTC ISO strings
    const startDateISO = eventStartDate.value ? new Date(eventStartDate.value).toISOString() : null;
    const endDateISO = eventEndDate.value ? new Date(eventEndDate.value).toISOString() : null;

    // Build the base query with account filter
    let query = `{"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}}`;
    
    // Add log_type filter if selected
    if (selectedLogType.value) {
      query += `,"log_type":{"$eq":"${selectedLogType.value}"}`;
    }
    
    // Add description filter if provided
    if (descriptionFilter.value.trim()) {
      query += `,"description":{"$like":"%${descriptionFilter.value.trim()}%"}`;
    }

   
    // Add date filter(s) if provided
    if (startDateISO && endDateISO) {
      // Both dates provided - use $and with separate conditions
      query += `,"$and":[{"created_on":{"$gte":{"$date":"${encodeURIComponent(startDateISO)}"}}},{"created_on":{"$lte":{"$date":"${encodeURIComponent(endDateISO)}"}}}]`;
    } else if (startDateISO) {
      // Only start date provided
      query += `,"created_on":{"$gte":{"$date":"${encodeURIComponent(startDateISO)}"}}`;
    } else if (endDateISO) {
      // Only end date provided
      query += `,"created_on":{"$lte":{"$date":"${encodeURIComponent(endDateISO)}"}}`;
    }
    
    query += '}';
    
    // Build count endpoint URL with the same UTC values
    let countUrl = `/crbn-tech/logs/count?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}}}`;
    if (selectedLogType.value) {
      countUrl += `&log_type=${encodeURIComponent(selectedLogType.value)}`;
    }
    if (descriptionFilter.value.trim()) {
      countUrl += `&description=${encodeURIComponent(descriptionFilter.value.trim())}`;
    }
    if (startDateISO) {
      countUrl += `&start_date=${encodeURIComponent(startDateISO)}`;
    }
    if (endDateISO) {
      countUrl += `&end_date=${encodeURIComponent(endDateISO)}`;
    }
    
    const items = await apiGetAll(`/crbn-tech/logs?q=${query}&offset=${eventsFirst.value}`);
    const countItems = await apiGetAll(countUrl);
    events.value = items || [];
    eventsTotalCount.value = countItems[0].count || 0;
  } catch (error) {
    console.error('Error loading events:', error);
  } finally {
    loading.value.events = false;
  }
}

async function loadLogTypes() {
  loading.value.logTypes = true;
  try {
    const items = await apiGetAll(`/crbn-tech/logs/types?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}}}`);
    // Extract log_type from each JSON item in the response
    logTypes.value = (items || []).map(item => item.log_type).filter(Boolean);
  } catch (error) {
    console.error('Error loading log types:', error);
  } finally {
    loading.value.logTypes = false;
  }
}


async function loadDAU(query) {
  loading.value.dau = true;
  dailyActiveUsers.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/dau${query}`);
    dailyActiveUsers.value = items || [];
  } catch (error) {
    console.error('Error loading DAU metrics:', error);
  } finally {
    loading.value.dau = false;
  }
}

async function loadWAU(query) {
  loading.value.wau = true;
  weeklyActiveUsers.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/wau${query}`);
    weeklyActiveUsers.value = items || [];
  } catch (error) {
    console.error('Error loading WAU metrics:', error);
  } finally {
    loading.value.wau = false;
  }
}

async function loadMAU(query) {
  loading.value.mau = true;
  monthlyActiveUsers.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/mau${query}`);
    monthlyActiveUsers.value = items || [];
  } catch (error) {
    console.error('Error loading MAU metrics:', error);
  } finally {
    loading.value.mau = false;
  }
}

async function loadTotalTokens(query) {
  loading.value.totalTokens = true;
  totalTokens.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/tokens${query}`);
    totalTokens.value = items || [];
  } catch (error) {
    console.error('Error loading total tokens metrics:', error);
  } finally {
    loading.value.totalTokens = false;
  }
}

async function loadUserTokens(query) {
  loading.value.userTokens = true;
  userTokens.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/tokens/user${query}`);
    userTokens.value = items || [];
  } catch (error) {
    console.error('Error loading user tokens metrics:', error);
  } finally {
    loading.value.userTokens = false;
  }
}

async function loadUserGroupTokens(query) {
  loading.value.userGroupTokens = true;
  userGroupTokens.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/tokens/user-group${query}`);
    userGroupTokens.value = items || [];
  } catch (error) {
    console.error('Error loading user group tokens metrics:', error);
  } finally {
    loading.value.userGroupTokens = false;
  }
}

async function loadMessageTokens(query) {
  loading.value.messageTokens = true;
  messageTokens.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/tokens/message${query}`);
    messageTokens.value = items || [];
  } catch (error) {
    console.error('Error loading message tokens metrics:', error);
  } finally {
    loading.value.messageTokens = false;
  }
}

async function loadConversationTokens(query) {
  loading.value.conversationTokens = true;
  conversationTokens.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/tokens/conversation${query}`);
    conversationTokens.value = items || [];
  } catch (error) {
    console.error('Error loading conversation tokens metrics:', error);
  } finally {
    loading.value.conversationTokens = false;
  }
}

async function loadConversationResponses(query) {
  loading.value.conversationResponses = true;
  conversationResponses.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/conversation/responses${query}`);
    conversationResponses.value = items || [];
  } catch (error) {
    console.error('Error loading conversation responses metrics:', error);
  } finally {
    loading.value.conversationResponses = false;
  }
}

async function loadConversationCount(query) {
  loading.value.conversationCount = true;
  conversationCount.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/conversation/count${query}`);
    conversationCount.value = items || [];
  } catch (error) {
    console.error('Error loading conversation count metrics:', error);
  } finally {
    loading.value.conversationCount = false;
  }
}

async function loadTotalMessages(query) {
  loading.value.totalMessages = true;
  totalMessages.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/messages${query}`);
    totalMessages.value = items || [];
  } catch (error) {
    console.error('Error loading total messages metrics:', error);
  } finally {
    loading.value.totalMessages = false;
  }
}

async function loadUserMessages(query) {
  loading.value.userMessages = true;
  userMessages.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/messages/user${query}`);
    userMessages.value = items || [];
  } catch (error) {
    console.error('Error loading user messages metrics:', error);
  } finally {
    loading.value.userMessages = false;
  }
}

async function loadUserGroupMessages(query) {
  loading.value.userGroupMessages = true;
  userGroupMessages.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/messages/user-group${query}`);
    userGroupMessages.value = items || [];
  } catch (error) {
    console.error('Error loading user group messages metrics:', error);
  } finally {
    loading.value.userGroupMessages = false;
  }
}

async function loadTopSkills(query) {
  loading.value.topSkills = true;
  topSkills.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/skills/top${query}`);
    topSkills.value = items || [];
  } catch (error) {
    console.error('Error loading top skills metrics:', error);
  } finally {
    loading.value.topSkills = false;
  }
}

async function loadFileSizeByExtension(query) {
  loading.value.fileSizeByExtension = true;
  fileSizeByExtension.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/file/size/extension${query}`);
    fileSizeByExtension.value = items || [];
  } catch (error) {
    console.error('Error loading file size by extension metrics:', error);
  } finally {
    loading.value.fileSizeByExtension = false;
  }
}

async function loadFileCountByExtension(query) {
  loading.value.fileCountByExtension = true;
  fileCountByExtension.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/file/count/extension${query}`);
    fileCountByExtension.value = items || [];
  } catch (error) {
    console.error('Error loading file count by extension metrics:', error);
  } finally {
    loading.value.fileCountByExtension = false;
  }
}

async function loadSharePointSites(query) {
  loading.value.sharepointSites = true;
  sharepointSites.value = [];
  try {
    const items = await apiGetAll(`/crbn-tech/metrics/knowledge-sources/sharepoint/sites${query}`);
    sharepointSites.value = items || [];
  } catch (error) {
    console.error('Error loading SharePoint sites metrics:', error);
  } finally {
    loading.value.sharepointSites = false;
  }
}

async function loadSharePointFiles(query) {
  loading.value.sharepointDiscoveredFiles = true;
  loading.value.sharepointSyncedFiles = true;
  loading.value.sharepointProcessableFiles = true;
  loading.value.sharepointProcessedFiles = true;
  
  sharepointDiscoveredFiles.value = [];
  sharepointSyncedFiles.value = [];
  sharepointProcessableFiles.value = [];
  sharepointProcessedFiles.value = [];
  
  try {
    const [
      discoveredResponse,
      syncedResponse,
      processableResponse,
      processedResponse
    ] = await Promise.all([
      apiGetAll(`/crbn-tech/metrics/knowledge-source/sharepoint/file/discovered${query}`),
      apiGetAll(`/crbn-tech/metrics/knowledge-source/sharepoint/file/synced${query}`),
      apiGetAll(`/crbn-tech/metrics/knowledge-source/sharepoint/file/processable${query}`),
      apiGetAll(`/crbn-tech/metrics/knowledge-source/sharepoint/file/processed${query}`)
    ]);

    sharepointDiscoveredFiles.value = discoveredResponse || [];
    sharepointSyncedFiles.value = syncedResponse || [];
    sharepointProcessableFiles.value = processableResponse || [];
    sharepointProcessedFiles.value = processedResponse || [];
  } catch (error) {
    console.error('Error loading SharePoint files metrics:', error);
  } finally {
    loading.value.sharepointDiscoveredFiles = false;
    loading.value.sharepointSyncedFiles = false;
    loading.value.sharepointProcessableFiles = false;
    loading.value.sharepointProcessedFiles = false;
  }
}

function maximizeChart(chartKey) {
  const config = chartConfigs[chartKey];
  if (!config) {
    console.error('Chart config not found for key:', chartKey);
    return;
  }
  
  maximizedChart.value = chartKey;
  maximizedChartTitle.value = config.title;
  maximizedChartType.value = config.type;
  
  // Get the chart data and options dynamically
  maximizedChartData.value = eval(config.data).value;
  
  // Create enhanced options for maximized view
  const baseOptions = eval(config.options);
  maximizedChartOptions.value = {
    ...baseOptions,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...baseOptions.plugins,
      legend: {
        ...baseOptions.plugins?.legend,
        labels: {
          ...baseOptions.plugins?.legend?.labels,
          font: {
            size: 14
          }
        }
      }
    },
    scales: baseOptions.scales ? {
      ...baseOptions.scales,
      x: baseOptions.scales.x ? {
        ...baseOptions.scales.x,
        ticks: {
          ...baseOptions.scales.x.ticks,
          font: {
            size: 12
          }
        }
      } : undefined,
      y: baseOptions.scales.y ? {
        ...baseOptions.scales.y,
        ticks: {
          ...baseOptions.scales.y.ticks,
          font: {
            size: 12
          }
        }
      } : undefined
    } : undefined
  };
}

function closeMaximizedChart() {
  maximizedChart.value = null;
  maximizedChartTitle.value = '';
  maximizedChartType.value = 'line';
  maximizedChartData.value = {};
  maximizedChartOptions.value = {};
}

onMounted(() => {
  loadMetrics();
  loadEvents();
  loadLogTypes();
  loadCredits();
});

watch(selectedAccount, () => {
  loadMetrics();
  loadEvents();
  loadLogTypes();
  loadCredits();
});

async function applyEventFilters() {
  // Reset to first page when applying filters
  eventsFirst.value = 0;
  // Load events with current filters
  await loadEvents();
}

function clearEventFilters() {
  selectedLogType.value = null;
  descriptionFilter.value = '';
  eventStartDate.value = null;
  eventEndDate.value = null;
}

function openEventDetails(event) {
  selectedEvent.value = event;
  showEventDetailsModal.value = true;
}

function closeEventDetails() {
  showEventDetailsModal.value = false;
  selectedEvent.value = null;
}

function closeFileDetails() {
  showFileDetailsModal.value = false;
  selectedFile.value = null;
}

function getFileType(file) {
  if (!file) return 'Unknown File Type';
  
  // Try to use MIME type first if available
  if (file.metadata?.mimeType) {
    const mimeType = file.metadata.mimeType.toLowerCase();
    
    // MIME type mappings for common file types
    const mimeTypeMap = {
      // PDF files
      'application/pdf': 'PDF Document',
      
      // Microsoft Word documents
      'application/msword': 'Microsoft Word Document',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Microsoft Word Document',
      'application/vnd.ms-word.document.macroEnabled.12': 'Microsoft Word Document (Macro Enabled)',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.template': 'Microsoft Word Template',
      
      // Microsoft Excel files
      'application/vnd.ms-excel': 'Microsoft Excel Spreadsheet',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Microsoft Excel Spreadsheet',
      'application/vnd.ms-excel.sheet.macroEnabled.12': 'Microsoft Excel Spreadsheet (Macro Enabled)',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.template': 'Microsoft Excel Template',
      
      // Microsoft PowerPoint files
      'application/vnd.ms-powerpoint': 'Microsoft PowerPoint Presentation',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'Microsoft PowerPoint Presentation',
      'application/vnd.ms-powerpoint.presentation.macroEnabled.12': 'Microsoft PowerPoint Presentation (Macro Enabled)',
      'application/vnd.openxmlformats-officedocument.presentationml.template': 'Microsoft PowerPoint Template',
      'application/vnd.openxmlformats-officedocument.presentationml.slideshow': 'Microsoft PowerPoint Slideshow',
      'application/vnd.ms-powerpoint.slideshow.macroEnabled.12': 'Microsoft PowerPoint Slideshow (Macro Enabled)',
      
      // Text files
      'text/plain': 'Text Document',
      'text/csv': 'Comma-Separated Values',
      'text/html': 'HTML Document',
      'text/xml': 'XML Document',
      'text/json': 'JSON Document',
      
      // Image files
      'image/jpeg': 'JPEG Image',
      'image/jpg': 'JPEG Image',
      'image/png': 'PNG Image',
      'image/gif': 'GIF Image',
      'image/bmp': 'Bitmap Image',
      'image/tiff': 'TIFF Image',
      'image/webp': 'WebP Image',
      'image/ico': 'Icon File',
      'image/x-icon': 'Icon File',
      
      // Audio files
      'audio/mpeg': 'MP3 Audio',
      'audio/mp3': 'MP3 Audio',
      'audio/mp4': 'MP4 Audio',
      'audio/m4a': 'M4A Audio',
      'audio/wav': 'WAV Audio',
      'audio/ogg': 'OGG Audio',
      'audio/aac': 'AAC Audio',
      
      // Video files
      'video/mp4': 'MP4 Video',
      'video/mpeg': 'MPEG Video',
      'video/quicktime': 'QuickTime Video',
      'video/webm': 'WebM Video',
      'video/avi': 'AVI Video',
      'video/x-msvideo': 'AVI Video',
      'video/x-ms-wmv': 'Windows Media Video',
      
      // Archive files
      'application/zip': 'ZIP Archive',
      'application/x-rar-compressed': 'RAR Archive',
      'application/x-7z-compressed': '7-Zip Archive',
      'application/gzip': 'GZIP Archive',
      'application/x-tar': 'TAR Archive',
      
      // Code files
      'text/javascript': 'JavaScript File',
      'application/javascript': 'JavaScript File',
      'text/css': 'CSS Stylesheet',
      'application/json': 'JSON File',
      'text/xml': 'XML File',
      'application/xml': 'XML File',
      
      // Other common types
      'application/octet-stream': 'Binary File',
      'application/x-binary': 'Binary File'
    };
    
    if (mimeTypeMap[mimeType]) {
      return mimeTypeMap[mimeType];
    }
  }
  
  // Fallback to file extension
  const filename = file.filename || '';
  if (!filename) return 'Unknown';
  
  const ext = filename.split('.').pop().toLowerCase();
  
  const extensionMap = {
    // PDF files
    'pdf': 'PDF Document',
    
    // Microsoft Word documents
    'doc': 'Microsoft Word Document',
    'docx': 'Microsoft Word Document',
    'docm': 'Microsoft Word Document (Macro Enabled)',
    'dotx': 'Microsoft Word Template',
    
    // Microsoft Excel files
    'xls': 'Microsoft Excel Spreadsheet',
    'xlsx': 'Microsoft Excel Spreadsheet',
    'xlsm': 'Microsoft Excel Spreadsheet (Macro Enabled)',
    'xlt': 'Microsoft Excel Template',
    'xltx': 'Microsoft Excel Template',
    
    // Microsoft PowerPoint files
    'ppt': 'Microsoft PowerPoint Presentation',
    'pptx': 'Microsoft PowerPoint Presentation',
    'pptm': 'Microsoft PowerPoint Presentation (Macro Enabled)',
    'pot': 'Microsoft PowerPoint Template',
    'potx': 'Microsoft PowerPoint Template',
    'potm': 'Microsoft PowerPoint Template (Macro Enabled)',
    'pps': 'Microsoft PowerPoint Slideshow',
    'ppsx': 'Microsoft PowerPoint Slideshow',
    'ppsm': 'Microsoft PowerPoint Slideshow (Macro Enabled)',
    
    // Text files
    'txt': 'Text Document',
    'csv': 'Comma-Separated Values',
    'html': 'HTML Document',
    'htm': 'HTML Document',
    'xml': 'XML Document',
    'json': 'JSON Document',
    
    // Image files
    'jpg': 'JPEG Image',
    'jpeg': 'JPEG Image',
    'png': 'PNG Image',
    'gif': 'GIF Image',
    'bmp': 'Bitmap Image',
    'tif': 'TIFF Image',
    'tiff': 'TIFF Image',
    'webp': 'WebP Image',
    'ico': 'Icon File',
    'icon': 'Icon File',
    
    // Audio files
    'mp3': 'MP3 Audio',
    'm4a': 'M4A Audio',
    'wav': 'WAV Audio',
    'ogg': 'OGG Audio',
    'aac': 'AAC Audio',
    'flac': 'FLAC Audio',
    
    // Video files
    'mp4': 'MP4 Video',
    'mov': 'QuickTime Video',
    'webm': 'WebM Video',
    'avi': 'AVI Video',
    'wmv': 'Windows Media Video',
    'mkv': 'Matroska Video',
    'flv': 'Flash Video',
    
    // Archive files
    'zip': 'ZIP Archive',
    'rar': 'RAR Archive',
    '7z': '7-Zip Archive',
    'tar': 'TAR Archive',
    'gz': 'GZIP Archive',
    
    // Code files
    'js': 'JavaScript File',
    'css': 'CSS Stylesheet',
    'php': 'PHP Script',
    'py': 'Python Script',
    'java': 'Java Source File',
    'cpp': 'C++ Source File',
    'c': 'C Source File',
    'cs': 'C# Source File',
    'rb': 'Ruby Script',
    'pl': 'Perl Script',
    'sh': 'Shell Script',
    'bat': 'Batch File',
    'ps1': 'PowerShell Script',
    
    // Data files
    'sql': 'SQL Script',
    'db': 'Database File',
    'sqlite': 'SQLite Database',
    'mdb': 'Microsoft Access Database',
    'accdb': 'Microsoft Access Database',
    
    // Configuration files
    'ini': 'Configuration File',
    'cfg': 'Configuration File',
    'conf': 'Configuration File',
    'config': 'Configuration File',
    'yaml': 'YAML File',
    'yml': 'YAML File',
    'toml': 'TOML File',
    
    // Font files
    'ttf': 'TrueType Font',
    'otf': 'OpenType Font',
    'woff': 'Web Open Font',
    'woff2': 'Web Open Font 2.0',
    'eot': 'Embedded OpenType Font'
  };
  
  if (extensionMap[ext]) {
    return extensionMap[ext];
  }
  
  // If extension not found, return a generic description
  return `.${ext.toUpperCase()} File`;
}

function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}


function onNodeDoubleClick(node) {
  // Only open modal for files (leaf nodes)
  // Skip knowledge source nodes, site nodes, drive nodes, and folder nodes
  if (node.leaf && node.data?.type !== 'drive' && node.data?.type !== 'site' && node.data?.type !== 'knowledgeSource') {
    selectedFile.value = node.data;
    showFileDetailsModal.value = true;
    // Load access grants for this file
    loadFileAccessGrants(node.data.id);
  }
}

function getSyncStatus(metadata) {
  if (!metadata) return 'Unknown';
  
  if (metadata.embeddingStatus === 'processed') {
    return 'Processed';
  } else if (metadata.embeddingStatus === 'pending') {
    return 'Processing...';
  } else if (!metadata.embeddingStatus && metadata.uploadStatus === 'pending') {
    return 'Uploading...';
  } else if (!metadata.embeddingStatus && metadata.uploadStatus === 'completed') {
    return 'Uploaded';
  } else {
    return 'Unknown';
  }
}

function openExternal(webUrl) {
  if (webUrl) {
    window.open(webUrl, '_blank');
  }
}

const downloadingFile = ref(false);
const fileAccessGrants = ref([]);
const loadingAccessGrants = ref(false);

async function downloadFile(fileId, filename) {
  if (!fileId || !filename) return;
  
  downloadingFile.value = true;
  
  try {
    const response = await fetch(import.meta.env.VITE_API_BASE_URL + `/v1/files/${fileId}/contents`, {
      method: 'GET',
      headers: {
        //'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        'Authorization': `Bearer ${import.meta.env.VITE_API_TOKEN}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // Get the file as a blob
    const blob = await response.blob();
    
    // Create a download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    
    // Trigger the download
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Error downloading file:', error);
    // You might want to show a user-friendly error message here
  } finally {
    downloadingFile.value = false;
  }
}

async function loadFileAccessGrants(fileId) {
  if (!fileId) return;
  
  loadingAccessGrants.value = true;
  fileAccessGrants.value = [];
  
  try {
    const query = `?q={"cephisso_platform_accounts_id":{"$eq":${selectedAccount.value}},"files_id":{"$eq":${fileId}}}`;
    const grants = await apiGetAll(`/v1/file-directory-access-grants${query}`);
    fileAccessGrants.value = grants;
  } catch (error) {
    console.error('Error loading file access grants:', error);
  } finally {
    loadingAccessGrants.value = false;
  }
}

function getAccessLabel(grant) {
  if (grant.update) {
    return 'Read/Write';
  } else if (grant.read) {
    return 'Read';
  } else {
    return 'None';
  }
}
</script>

<style scoped>
.usage-metrics {
  padding: 1rem;
}

.usage-metrics :deep(.p-tabview .p-tabview-panels) {
  background: inherit;
  color: inherit;
}

.content {
  margin-top: 1rem;
}

.timeframe-selector {
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.timeframe-selector label {
  font-weight: bold;
}

.timeframe-selector select {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-width: 200px;
}

.custom-date-range {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  width: 100%;
}

.date-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.date-input label {
  font-weight: normal;
  white-space: nowrap;
}

.date-input input {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.credits-dashboard {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.credits-dashboard-card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;
}

.credits-dashboard-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.credits-dashboard-card-title {
  font-weight: 600;
  font-size: 1rem;
  color: #333;
  margin-bottom: 0.5rem;
}

.credits-dashboard-card-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #2563eb;
}

.credits-dashboard-card-hint {
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.5rem;
}

.credits-modal-actions {
  margin-bottom: 1rem;
}

.charts-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.chart-card {
  background: white;
  padding: 1rem;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chart-card h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  color: #333;
}

h2 {
  margin-top: 2rem;
  margin-bottom: 1rem;
}

.mt-4 {
  margin-top: 2rem;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.maximize-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}



.events-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.events-filters label {
  font-weight: bold;
  white-space: nowrap;
}

.events-filters select {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-width: 200px;
}

.events-filters input {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-width: 200px;
}

.event-details-content {
  padding: 1rem;
}

.event-details {
  margin-bottom: 1rem;
}

.event-detail-row {
  margin-bottom: 0.5rem;
}

.metadata-content {
  background-color: #f9f9f9;
  padding: 0.5rem;
  border-radius: 4px;
}

.files-section {
  padding: 1rem 0;
}

.file-directories-container {
  margin-top: 1rem;
}

.file-directories-tree {
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 1rem;
}

.file-directories-tree :deep(.p-treenode-content) {
  outline: none !important;
}

.tree-node {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.folder-icon {
  font-size: 1.1em;
}

.knowledge-source-icon {
  font-size: 1.2em;
}

.site-icon {
  font-size: 1.1em;
}

.drive-icon {
  font-size: 1.1em;
}

.file-icon {
  font-size: 1.1em;
}

.search-icon {
  font-size: 0.9em;
  color: #6b7280;
  cursor: pointer;
  margin-left: 0.5rem;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.search-icon:hover {
  opacity: 1;
  color: #3b82f6;
}

.metadata-content {
  background-color: #f9f9f9;
  padding: 0.5rem;
  border-radius: 4px;
}

.file-details-content {
  padding: 1rem;
}

.file-details {
  margin-bottom: 1rem;
}

.file-detail-row {
  margin-bottom: 0.5rem;
}

.metadata-pre {
  white-space: pre-wrap;
  word-break: break-word;
}

.access-grants-content {
  margin-top: 0.5rem;
}

.access-grants-table {
  font-size: 0.9rem;
}

.mb {
  margin-bottom: 1rem;
}

.ml {
  margin-left: 0.5rem;
}

/* Style for knowledge source nodes to make them look like main headers */
.file-directories-tree :deep(.p-treenode:has(.p-treenode-content > .p-treenode-label > .knowledge-source-node) > .p-treenode-content) {
  font-weight: 700;
  font-size: 1.2rem;
}
</style> 